// Specialist Template Library page — Template Library Sub-commit 3 of 5
// Two top-level tabs: My Exercises + My Homeworks. Card list per tab with
// edit/delete affordances. Dedicated edit modal for exercise templates.
// Minimal rename/description edit modal for homework templates (snapshot
// semantics — exercise list is frozen at save-time per yesterday's
// investigation locked decision).

import { injectHomeworkStyles } from './styles.js';
import {
  loadExerciseTemplates, saveExerciseTemplate, updateExerciseTemplate, deleteExerciseTemplate,
  loadHomeworkTemplates, loadHomeworkTemplate, updateHomeworkTemplate, deleteHomeworkTemplate,
} from './templates.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;

export function renderTemplatesPage({ isWeb, childId }) {
  injectHomeworkStyles();
  const H = window.HUD || {};
  const { el, mkBtn, S, re, DB } = H;

  if (!S._templatesView) S._templatesView = 'exercises';

  const sec = el('div', { class: 'section' });

  // Back bar
  const child = childId ? (DB?.children?.find(c => c.id === childId)) : null;
  const backLabel = child?.name ? '← ' + (T('templates_back_to_homework', { name: child.name }) || ('Back to ' + child.name + '’s Home Exercises'))
                                : '← ' + (T('templates_back_to_homework_generic') || 'Back to Home Exercises');
  const backRow = el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1.5px solid #e2e8f0' } });
  const backBtn = el('button', { style: { background: 'none', border: 'none', cursor: 'pointer', color: '#0d9488', fontWeight: 700, fontSize: '.84rem', fontFamily: 'inherit', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '4px' } }, [backLabel]);
  backBtn.onclick = () => { S._hwTemplatesView = false; S._hwTemplatesViewChild = null; re(); };
  backRow.appendChild(backBtn);
  sec.appendChild(backRow);

  // Header
  const hd = el('div', { class: 'sec-hd' });
  hd.appendChild(el('div', { class: 'sec-hd-left' }, [
    el('h2', { class: 'page-title' }, [T('nav_templates') || 'Templates']),
    el('p', { class: 'page-sub' }, [T('templates_subtitle') || 'Reusable exercises and homeworks for your patients']),
  ]));
  const right = el('div', { class: 'sec-hd-right' });
  // + New button is exercise-only; rendered conditionally via _refresh based on active tab
  hd.appendChild(right);
  sec.appendChild(hd);

  // Loading host
  const host = el('div');
  host.appendChild(el('div', { style: { textAlign: 'center', padding: '24px', color: '#64748b' } }, [T('btn_loading') || 'Loading...']));
  sec.appendChild(host);

  _refresh(host, H, right, childId);
  return sec;
}

async function _refresh(host, H, headerRight, childId) {
  const { el, mkBtn, S } = H;

  const [exTemplates, hwTemplates] = await Promise.all([
    loadExerciseTemplates(),
    loadHomeworkTemplates(),
  ]);

  host.innerHTML = '';

  // Tab bar
  host.appendChild(_renderTabBar(S._templatesView, exTemplates.length, hwTemplates.length, H, () => _refresh(host, H, headerRight, childId)));

  // + New button only on My Exercises tab
  if (headerRight) {
    headerRight.innerHTML = '';
    if (S._templatesView === 'exercises') {
      headerRight.appendChild(mkBtn(T('btn_new') || '+ New', 'btn-md btn-primary',
        () => _openExerciseTemplateEditModal(null, H, () => _refresh(host, H, headerRight, childId))));
    }
  }

  if (S._templatesView === 'exercises') {
    host.appendChild(_renderExerciseTemplatesList(exTemplates, H, () => _refresh(host, H, headerRight, childId), childId));
  } else {
    host.appendChild(_renderHomeworkTemplatesList(hwTemplates, H, () => _refresh(host, H, headerRight, childId), childId));
  }
}

function _renderTabBar(view, exCount, hwCount, H, onSwitch) {
  const { el, S } = H;
  const bar = el('div', { style: { display: 'flex', gap: '8px', marginBottom: '18px' } });
  const tabs = [
    ['exercises', T('templates_my_exercises') || 'My Exercises', exCount],
    ['homeworks', T('templates_my_homeworks') || 'My Homeworks', hwCount],
  ];
  tabs.forEach(([key, label, count]) => {
    const active = view === key;
    const btn = el('button', { class: active ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-ghost' }, [label]);
    btn.appendChild(el('span', { class: 'patient-count-badge' }, [String(count)]));
    btn.onclick = () => { S._templatesView = key; onSwitch(); };
    bar.appendChild(btn);
  });
  return bar;
}

// ── Exercise templates list ──

function _renderExerciseTemplatesList(templates, H, onChanged, childId) {
  const { el } = H;
  const wrap = el('div');

  if (!templates.length) {
    wrap.appendChild(el('div', { class: 'empty-state' }, [
      el('span', { class: 'empty-state-icon' }, ['📚']),
      el('div', { class: 'empty-state-title' }, [T('templates_no_exercises_title') || 'No exercise templates yet']),
      el('div', { class: 'empty-state-body' }, [T('templates_no_exercises_body') || 'Create your first by tapping + New, or by saving an exercise from a homework’s create form.']),
    ]));
    return wrap;
  }

  templates.forEach(tmpl => {
    wrap.appendChild(_renderExerciseCard(tmpl, H, onChanged, childId));
  });
  return wrap;
}

function _renderExerciseCard(tmpl, H, onChanged, childId) {
  const { el } = H;
  const card = el('div', { style: {
    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '12px',
    padding: '12px 14px', marginBottom: '10px',
    display: 'flex', alignItems: 'center', gap: '10px',
    cursor: 'pointer', transition: 'box-shadow .12s, border-color .12s',
  } });
  card.onmouseenter = () => { card.style.borderColor = '#0d9488'; };
  card.onmouseleave = () => { card.style.borderColor = '#e2e8f0'; };

  const info = el('div', { style: { flex: '1', minWidth: '0' } });
  info.appendChild(el('div', { style: { fontWeight: 600, fontSize: '14px', color: '#0f1a18' } }, [tmpl.name || '(unnamed)']));
  const measure = _exerciseMeasure(tmpl);
  if (measure) info.appendChild(el('div', { style: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' } }, [measure]));
  card.appendChild(info);

  const actions = el('div', { style: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: '0' } });
  actions.onclick = (e) => e.stopPropagation();

  if (childId) {
    const useBtn = el('button', { class: 'btn btn-sm btn-primary', style: { padding: '6px 12px' } }, [T('btn_use') || 'Use']);
    useBtn.onclick = () => _useExerciseTemplate(tmpl, childId, H);
    actions.appendChild(useBtn);
  }
  actions.appendChild(_iconBtn('✎', T('btn_edit') || 'Edit', () => _openExerciseTemplateEditModal(tmpl, H, onChanged)));
  actions.appendChild(_iconBtn('⋮', T('hw3_more_actions') || 'More actions', (btn) => {
    _showRowKebab(btn, [
      { icon: '🗑', label: T('btn_delete') || 'Delete', onClick: () => _confirmDeleteExercise(tmpl, H, onChanged) },
    ], H);
  }));
  card.appendChild(actions);

  card.onclick = () => _openExerciseTemplateEditModal(tmpl, H, onChanged);
  return card;
}

function _exerciseMeasure(tmpl) {
  if (tmpl.sets && tmpl.reps) return tmpl.sets + '×' + tmpl.reps + ' reps';
  if (tmpl.duration_seconds) {
    const mins = Math.round(tmpl.duration_seconds / 60);
    if (mins >= 1) return mins + ' min';
    return tmpl.duration_seconds + ' sec';
  }
  if (tmpl.measure_unit) return tmpl.measure_unit;
  return '';
}

// ── Homework templates list ──

function _renderHomeworkTemplatesList(templates, H, onChanged, childId) {
  const { el } = H;
  const wrap = el('div');

  if (!templates.length) {
    wrap.appendChild(el('div', { class: 'empty-state' }, [
      el('span', { class: 'empty-state-icon' }, ['📋']),
      el('div', { class: 'empty-state-title' }, [T('templates_no_homeworks_title') || 'No homework templates yet']),
      el('div', { class: 'empty-state-body' }, [T('templates_no_homeworks_body') || 'Save an existing homework from the create form to start your library.']),
    ]));
    return wrap;
  }

  templates.forEach(tmpl => {
    wrap.appendChild(_renderHomeworkCard(tmpl, H, onChanged, childId));
  });
  return wrap;
}

function _renderHomeworkCard(tmpl, H, onChanged, childId) {
  const { el } = H;
  const card = el('div', { style: {
    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '12px',
    padding: '12px 14px', marginBottom: '10px',
    display: 'flex', alignItems: 'center', gap: '10px',
    cursor: 'pointer', transition: 'border-color .12s',
  } });
  card.onmouseenter = () => { card.style.borderColor = '#0d9488'; };
  card.onmouseleave = () => { card.style.borderColor = '#e2e8f0'; };

  const info = el('div', { style: { flex: '1', minWidth: '0' } });
  info.appendChild(el('div', { style: { fontWeight: 600, fontSize: '14px', color: '#0f1a18' } }, [tmpl.title || '(unnamed)']));
  const exCount = (tmpl.exercises_json || []).length;
  const subtitle = exCount + ' ' + (exCount === 1 ? 'exercise' : 'exercises');
  info.appendChild(el('div', { style: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' } }, [subtitle]));
  card.appendChild(info);

  const actions = el('div', { style: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: '0' } });
  actions.onclick = (e) => e.stopPropagation();

  if (childId) {
    const useBtn = el('button', { class: 'btn btn-sm btn-primary', style: { padding: '6px 12px' } }, [T('btn_use') || 'Use']);
    useBtn.onclick = () => _useHomeworkTemplate(tmpl, childId, H);
    actions.appendChild(useBtn);
  }
  actions.appendChild(_iconBtn('✎', T('btn_edit') || 'Edit', () => _openHomeworkTemplateEditModal(tmpl, H, onChanged)));
  actions.appendChild(_iconBtn('⋮', T('hw3_more_actions') || 'More actions', (btn) => {
    _showRowKebab(btn, [
      { icon: '🗑', label: T('btn_delete') || 'Delete', onClick: () => _confirmDeleteHomework(tmpl, H, onChanged) },
    ], H);
  }));
  card.appendChild(actions);

  card.onclick = () => _openHomeworkTemplateEditModal(tmpl, H, onChanged);
  return card;
}

// ── Use handlers — open create-homework modal pre-filled, scoped to current patient ──

function _useExerciseTemplate(tmpl, childId, H) {
  const { S, re } = H;
  // Wrap the exercise template into a homework-template-shaped object that
  // create-modal already understands. Title/description blank; schedule blank
  // (specialist fills in); exercises_json = single exercise from this template.
  const synthTemplate = {
    title: '',
    description: '',
    exercises_json: [{
      title: tmpl.name || '',
      instructions: tmpl.instructions || '',
      reps: tmpl.reps ?? null,
      sets: tmpl.sets ?? null,
      durationSeconds: tmpl.duration_seconds ?? null,
      measureUnit: tmpl.measure_unit || null,
      // SC-A1c (TP4): carry the template's attachments (table columns are snake_case) into the
      // exercises_json (camelCase) so hydration + the _doSave copy pass land them on the child.
      attachedFilePaths: tmpl.attached_file_paths ? [...tmpl.attached_file_paths] : [],
      attachedFileNames: tmpl.attached_file_names ? [...tmpl.attached_file_names] : [],
    }],
  };
  S._hwTemplatesView = false;
  S._hwTemplatesViewChild = null;
  re();
  window.HUD_HOMEWORK.mountHomeworkCreateModal({ childId, template: synthTemplate });
}

function _useHomeworkTemplate(tmpl, childId, H) {
  const { S, re } = H;
  S._hwTemplatesView = false;
  S._hwTemplatesViewChild = null;
  re();
  window.HUD_HOMEWORK.mountHomeworkCreateModal({ childId, template: tmpl });
}

// ── Shared row helpers ──

function _iconBtn(icon, title, onclick) {
  const b = document.createElement('button');
  b.textContent = icon;
  b.title = title;
  b.style.cssText = 'background:none;border:none;cursor:pointer;font-size:16px;padding:4px;border-radius:6px;min-width:32px;min-height:32px;display:flex;align-items:center;justify-content:center;transition:background .12s;';
  b.onmouseenter = () => { b.style.background = '#f1f5f9'; };
  b.onmouseleave = () => { b.style.background = 'none'; };
  b.onclick = (e) => { e.stopPropagation(); onclick(b); };
  return b;
}

function _showRowKebab(anchor, items, H) {
  const { el } = H;
  document.querySelectorAll('.hw-lib-kebab').forEach(m => m.remove());
  const menu = el('div', { class: 'long-press-menu hw-lib-kebab' });
  items.forEach(it => {
    const row = el('div', { class: 'long-press-item' });
    row.appendChild(el('span', { style: { fontSize: '1rem' } }, [it.icon || '']));
    row.appendChild(el('span', { style: { flex: '1' } }, [it.label]));
    row.onclick = (e) => {
      e.stopPropagation(); menu.remove();
      try { it.onClick && it.onClick(); } catch (err) { console.error('❌ kebab action:', err); }
    };
    menu.appendChild(row);
  });
  document.body.appendChild(menu);
  const rect = anchor.getBoundingClientRect();
  const mw = 200, mh = items.length * 48 + 12;
  let x = Math.min(rect.right - mw, window.innerWidth - mw - 12);
  x = Math.max(12, x);
  let y = rect.bottom + 8;
  if (y + mh > window.innerHeight - 12) y = rect.top - mh - 8;
  y = Math.max(12, y);
  menu.style.left = x + 'px';
  menu.style.top = y + 'px';
  setTimeout(() => {
    const close = () => {
      menu.remove();
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
    const onPointer = (ev) => { if (!menu.contains(ev.target)) close(); };
    const onKey = (ev) => { if (ev.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
  }, 50);
}

// ── Exercise template edit modal (dedicated, per locked decision F2) ──

function _openExerciseTemplateEditModal(tmpl, H, onSaved) {
  const { el, openModal, toast, SB, session } = H;
  const isEdit = !!tmpl;
  const title = isEdit ? (T('templates_edit_exercise') || 'Edit Exercise Template') : (T('templates_new_exercise') || 'New Exercise Template');

  // Local form state — clone from template or blank
  const state = {
    name: tmpl?.name || '',
    instructions: tmpl?.instructions || '',
    reps: tmpl?.reps ?? null,
    sets: tmpl?.sets ?? null,
    durationSeconds: tmpl?.duration_seconds ?? null,
    measureUnit: tmpl?.measure_unit || '',
    attachedFilePaths: [...(tmpl?.attached_file_paths || [])],
    attachedFileNames: [...(tmpl?.attached_file_names || [])],
  };

  openModal(title, (mb, close) => {
    // NAME
    mb.appendChild(el('div', { class: 'hw2-section-label', style: { marginTop: '0' } }, [T('templates_field_name') || 'Name']));
    const nameInp = el('input', { type: 'text', class: 'hw2-input', placeholder: T('templates_name_placeholder') || 'e.g. /R/ practice', maxlength: '200' });
    nameInp.value = state.name;
    nameInp.oninput = () => { state.name = nameInp.value; _updateSaveBtn(); };
    mb.appendChild(nameInp);

    // INSTRUCTIONS
    mb.appendChild(el('div', { class: 'hw2-section-label' }, [T('templates_field_instructions') || 'Instructions']));
    const insInp = el('textarea', { class: 'hw2-input hw2-textarea', placeholder: T('templates_instructions_placeholder') || 'Optional instructions for the parent', rows: '3' });
    insInp.value = state.instructions;
    insInp.oninput = () => { state.instructions = insInp.value; };
    mb.appendChild(insInp);

    // MEASURE — reps/sets + duration + unit (all optional)
    mb.appendChild(el('div', { class: 'hw2-section-label' }, [T('templates_field_measure') || 'Measure']));
    const measureRow = el('div', { style: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' } });

    const repsBox = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } });
    repsBox.appendChild(el('span', { style: { fontSize: '12px', color: '#64748b', fontWeight: 600 } }, [T('templates_reps') || 'Reps']));
    const repsInp = el('input', { type: 'number', class: 'hw2-input', style: { width: '60px', textAlign: 'center', padding: '6px 4px' }, placeholder: '#' });
    repsInp.value = state.reps ?? '';
    repsInp.oninput = () => { state.reps = parseInt(repsInp.value) || null; };
    repsBox.appendChild(repsInp);
    measureRow.appendChild(repsBox);

    const setsBox = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } });
    setsBox.appendChild(el('span', { style: { fontSize: '12px', color: '#64748b', fontWeight: 600 } }, [T('templates_sets') || 'Sets']));
    const setsInp = el('input', { type: 'number', class: 'hw2-input', style: { width: '60px', textAlign: 'center', padding: '6px 4px' }, placeholder: '#' });
    setsInp.value = state.sets ?? '';
    setsInp.oninput = () => { state.sets = parseInt(setsInp.value) || null; };
    setsBox.appendChild(setsInp);
    measureRow.appendChild(setsBox);

    const durBox = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } });
    durBox.appendChild(el('span', { style: { fontSize: '12px', color: '#64748b', fontWeight: 600 } }, [T('templates_duration') || 'Duration']));
    const durInp = el('input', { type: 'number', class: 'hw2-input', style: { width: '70px', textAlign: 'center', padding: '6px 4px' }, placeholder: T('templates_min') || 'min' });
    durInp.value = state.durationSeconds ? Math.round(state.durationSeconds / 60) : '';
    durInp.oninput = () => {
      const v = parseInt(durInp.value);
      state.durationSeconds = (v > 0) ? v * 60 : null;
    };
    durBox.appendChild(durInp);
    durBox.appendChild(el('span', { style: { fontSize: '11px', color: '#94a3b8' } }, [T('templates_min') || 'min']));
    measureRow.appendChild(durBox);

    mb.appendChild(measureRow);

    // UNIT (optional free text)
    const unitWrap = el('div', { style: { marginBottom: '12px' } });
    unitWrap.appendChild(el('span', { style: { fontSize: '12px', color: '#64748b', fontWeight: 600, marginRight: '8px' } }, [T('templates_field_unit') || 'Custom unit']));
    const unitInp = el('input', { type: 'text', class: 'hw2-input', style: { width: '160px', display: 'inline-block' }, placeholder: T('templates_unit_placeholder') || 'e.g. words, breaths' });
    unitInp.value = state.measureUnit;
    unitInp.oninput = () => { state.measureUnit = unitInp.value; };
    unitWrap.appendChild(unitInp);
    mb.appendChild(unitWrap);

    // Helper text
    mb.appendChild(el('div', { style: { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', marginBottom: '14px' } }, [
      T('templates_measure_hint') || 'All measure fields are optional. Use whichever combination fits this exercise.',
    ]));

    // SC-A1d: managed attached-files list (upload + unlink). Device upload ONLY (ruling 4) to the
    // model-b prefix specialists/<uid>/templates (Pattern E — no policy change). Remove = UNLINK only:
    // it drops the entry from the template's arrays and NEVER deletes the storage object (A1c's dedup
    // means objects are shared across templates; orphan-on-delete is the app posture — ruling 3). Paths
    // only, no urls (exercise_templates has no url column). New + edit share this modal (ruling 8):
    // edit persists each change live via updateExerciseTemplate; new persists on Save via saveExerciseTemplate.
    mb.appendChild(el('div', { class: 'hw2-section-label' }, [T('templates_attached_files') || 'Attached files']));
    const filesList = el('div', { style: { marginBottom: '8px' } });
    function _renderFilesList() {
      filesList.textContent = '';
      if (!state.attachedFileNames.length) {
        filesList.appendChild(el('div', { style: { fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '2px 0' } }, [T('templates_no_attachments') || 'No files attached yet.']));
        return;
      }
      state.attachedFileNames.forEach((nm, i) => {
        const row = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155', padding: '2px 0' } });
        row.appendChild(el('span', { style: { fontSize: '12px', flexShrink: '0' } }, ['📎']));
        row.appendChild(el('span', { style: { flex: '1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, [nm || 'file']));
        const rm = el('button', { title: T('templates_remove_file') || 'Remove file', style: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: '1', flexShrink: '0', padding: '0 4px' } }, ['×']);
        rm.onclick = () => _removeFile(i);
        row.appendChild(rm);
        filesList.appendChild(row);
      });
    }
    async function _persistFiles() {
      // Edit persists live; new waits for Save (no row id yet).
      if (!isEdit) return;
      try { await updateExerciseTemplate(tmpl.id, { attachedFilePaths: state.attachedFilePaths, attachedFileNames: state.attachedFileNames }); }
      catch (e) { console.error('❌ template files persist:', e); toast?.(T('templates_save_failed') || 'Could not save template.', 'error'); }
    }
    async function _removeFile(i) {
      // UNLINK only — never delete the storage object (shared references exist by design).
      state.attachedFilePaths.splice(i, 1);
      state.attachedFileNames.splice(i, 1);
      _renderFilesList();
      await _persistFiles();
    }
    _renderFilesList();
    mb.appendChild(filesList);

    const attachBtn = el('button', { class: 'btn btn-sm btn-ghost', style: { marginBottom: '14px' } }, ['📎 ' + (T('hw2_attach_file') || 'Attach file')]);
    attachBtn.onclick = () => {
      if (attachBtn.disabled) return;
      const fi = document.createElement('input');
      fi.type = 'file'; fi.multiple = true; fi.accept = 'image/*,video/*,.pdf,.doc,.docx';
      fi.onchange = async (ev) => {
        attachBtn.disabled = true; attachBtn.textContent = T('storage_uploading') || 'Uploading…';
        try {
          for (const f of Array.from(ev.target.files)) {
            // model b, Pattern E: specialists/<uid>/templates/<ts>_<name>. Best-effort per file; a failed
            // upload is NOT pushed (nothing partial written) — successful ones are kept.
            const { path } = await SB.uploadFile('specialists/' + session.id + '/templates', f);
            state.attachedFilePaths.push(path);
            state.attachedFileNames.push(f.name);
          }
        } catch (e) {
          console.error('❌ template attach upload:', e);
          toast?.(T('hw2_ex_upload_error') || 'Could not upload file.', 'error');
        } finally {
          _renderFilesList();
          await _persistFiles(); // persist the files that DID upload (edit only)
          attachBtn.disabled = false; attachBtn.textContent = '📎 ' + (T('hw2_attach_file') || 'Attach file');
        }
      };
      fi.click();
    };
    mb.appendChild(attachBtn);

    // Action row
    const actRow = el('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' } });

    if (isEdit) {
      const delBtn = el('button', { class: 'btn btn-sm btn-ghost', style: { color: '#ef4444', marginRight: 'auto' } }, [T('btn_delete') || 'Delete']);
      delBtn.onclick = () => _confirmDeleteExercise(tmpl, H, () => { close(); onSaved && onSaved(); });
      actRow.appendChild(delBtn);
    }

    const cancelBtn = el('button', { class: 'btn btn-sm btn-ghost' }, [T('btn_cancel') || 'Cancel']);
    cancelBtn.onclick = close;
    actRow.appendChild(cancelBtn);

    const saveBtn = el('button', { class: 'btn btn-sm btn-primary' }, [T('btn_save') || 'Save']);
    saveBtn.onclick = async () => {
      if (!state.name.trim()) { toast?.(T('templates_name_required') || 'Name is required.', 'error'); return; }
      saveBtn.disabled = true; saveBtn.textContent = T('btn_loading') || 'Saving...';
      try {
        if (isEdit) {
          await updateExerciseTemplate(tmpl.id, state);
          toast?.(T('templates_saved') || 'Template saved.');
        } else {
          await saveExerciseTemplate(state);
          toast?.(T('templates_created') || 'Template created.');
        }
        close();
        onSaved && onSaved();
      } catch (e) {
        console.error('❌ save exercise template:', e);
        toast?.(T('templates_save_failed') || 'Could not save template.', 'error');
        saveBtn.disabled = false; saveBtn.textContent = T('btn_save') || 'Save';
      }
    };
    actRow.appendChild(saveBtn);
    mb.appendChild(actRow);

    function _updateSaveBtn() {
      const ok = state.name.trim().length > 0;
      saveBtn.disabled = !ok;
      saveBtn.style.opacity = ok ? '1' : '0.5';
    }
    _updateSaveBtn();
  }, 460);
}

// ── Homework template edit modal (minimal — rename/description/delete) ──

function _openHomeworkTemplateEditModal(tmpl, H, onSaved) {
  const { el, openModal, toast } = H;
  const isEdit = !!tmpl;
  const title = isEdit ? (T('templates_edit_homework') || 'Edit Homework Template') : (T('templates_new_homework') || 'New Homework Template');

  // For "+ New" homework template: not directly supported via this minimal modal.
  // Per locked decision: homework templates are saved via the existing
  // save-as-template checkbox in the homework create flow. Direct creation
  // from the library would need full exercise-list editing, deferred.
  if (!isEdit) {
    H.toast?.(T('templates_create_hw_hint') || 'To create a homework template, save one from the homework create form (Save as template).', 'info', 5000);
    return;
  }

  const state = {
    title: tmpl.title || '',
    description: tmpl.description || '',
  };

  openModal(title, (mb, close) => {
    // TITLE
    mb.appendChild(el('div', { class: 'hw2-section-label', style: { marginTop: '0' } }, [T('templates_field_name') || 'Name']));
    const titleInp = el('input', { type: 'text', class: 'hw2-input', maxlength: '200' });
    titleInp.value = state.title;
    titleInp.oninput = () => { state.title = titleInp.value; _updateSaveBtn(); };
    mb.appendChild(titleInp);

    // DESCRIPTION
    mb.appendChild(el('div', { class: 'hw2-section-label' }, [T('templates_field_description') || 'Description']));
    const descInp = el('textarea', { class: 'hw2-input hw2-textarea', rows: '3', placeholder: T('templates_description_placeholder') || 'Optional notes for the parent' });
    descInp.value = state.description;
    descInp.oninput = () => { state.description = descInp.value; };
    mb.appendChild(descInp);

    // Exercises summary (read-only — exercise list is frozen in v1)
    const exCount = (tmpl.exercises_json || []).length;
    if (exCount) {
      mb.appendChild(el('div', { class: 'hw2-section-label' }, [T('templates_field_exercises') || 'Exercises']));
      const exList = el('div', { style: { background: '#f0fdf9', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' } });
      (tmpl.exercises_json || []).forEach(ex => {
        const row = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155', padding: '2px 0' } });
        row.appendChild(el('span', { style: { color: '#0d9488', fontSize: '8px', flexShrink: '0' } }, ['●']));
        row.appendChild(el('span', { style: { flex: '1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, [ex.title || '(unnamed)']));
        exList.appendChild(row);
      });
      mb.appendChild(exList);
      mb.appendChild(el('div', { style: { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', marginBottom: '14px' } }, [
        T('templates_hw_frozen_hint') || 'The exercise list is frozen at save-time. To change exercises, save a new homework as a template.',
      ]));
    }

    // Action row
    const actRow = el('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' } });

    const delBtn = el('button', { class: 'btn btn-sm btn-ghost', style: { color: '#ef4444', marginRight: 'auto' } }, [T('btn_delete') || 'Delete']);
    delBtn.onclick = () => _confirmDeleteHomework(tmpl, H, () => { close(); onSaved && onSaved(); });
    actRow.appendChild(delBtn);

    const cancelBtn = el('button', { class: 'btn btn-sm btn-ghost' }, [T('btn_cancel') || 'Cancel']);
    cancelBtn.onclick = close;
    actRow.appendChild(cancelBtn);

    const saveBtn = el('button', { class: 'btn btn-sm btn-primary' }, [T('btn_save') || 'Save']);
    saveBtn.onclick = async () => {
      if (!state.title.trim()) { toast?.(T('templates_name_required') || 'Name is required.', 'error'); return; }
      saveBtn.disabled = true; saveBtn.textContent = T('btn_loading') || 'Saving...';
      try {
        await updateHomeworkTemplate(tmpl.id, state);
        toast?.(T('templates_saved') || 'Template saved.');
        close();
        onSaved && onSaved();
      } catch (e) {
        console.error('❌ save homework template:', e);
        toast?.(T('templates_save_failed') || 'Could not save template.', 'error');
        saveBtn.disabled = false; saveBtn.textContent = T('btn_save') || 'Save';
      }
    };
    actRow.appendChild(saveBtn);
    mb.appendChild(actRow);

    function _updateSaveBtn() {
      const ok = state.title.trim().length > 0;
      saveBtn.disabled = !ok;
      saveBtn.style.opacity = ok ? '1' : '0.5';
    }
    _updateSaveBtn();
  }, 460);
}

// ── Delete confirmations ──

function _confirmDeleteExercise(tmpl, H, onDone) {
  const { openConfirm, toast } = H;
  openConfirm(
    T('templates_delete_title') || 'Delete this template?',
    (T('templates_delete_body') || 'This template will be removed from your library. This cannot be undone.'),
    true,
    async () => {
      try {
        await deleteExerciseTemplate(tmpl.id);
        toast?.(T('templates_deleted') || 'Template deleted.');
        onDone && onDone();
      } catch (e) {
        console.error('❌ delete exercise template:', e);
        toast?.(T('templates_delete_failed') || 'Could not delete template.', 'error');
      }
    },
  );
}

function _confirmDeleteHomework(tmpl, H, onDone) {
  const { openConfirm, toast } = H;
  openConfirm(
    T('templates_delete_title') || 'Delete this template?',
    (T('templates_delete_body') || 'This template will be removed from your library. This cannot be undone.'),
    true,
    async () => {
      try {
        await deleteHomeworkTemplate(tmpl.id);
        toast?.(T('templates_deleted') || 'Template deleted.');
        onDone && onDone();
      } catch (e) {
        console.error('❌ delete homework template:', e);
        toast?.(T('templates_delete_failed') || 'Could not delete template.', 'error');
      }
    },
  );
}
